import { createMemo, createSignal, getOwner, runWithOwner, Show } from "solid-js";
import type SubformId from "../state/SubformId";
import { parseStringTemplate } from "../utils/stringTemplateUtil";
import Input from "../components/inputs/Input";
import { useTranslation } from "../state/i18n";
import { TextAndIconButton } from "../components/button/Button";
import { useFormServices } from "../services/formServices";
import SubformService from "../services/SubformService";
import { SingleSelect, SingleSelectInput } from "../components/menu/Select";
import { TextInput } from "../components/inputs/TextInput";
import MixedInput from "../components/inputs/MixedInput";
import { toFieldValueOptions, toVariableValueOption } from "../utils/selectOptionsUtil";
import { SUMMARIZABLE_VARIABLES } from "../variables/variableUtil";
import { SUMMARIZABLE_FIELDS } from "../utils/fieldUtil";
import type { ImageOption, SelectOption } from "../types/Menu";
import SubformShowEvent from "../events/SubformShowEvent";
import SubformCreateModal from "../pages/formEditor/subforms/SubformCreateModal";
import { useModalManager } from "../components/modal/ModalManager";
import { isNil } from "lodash-es";
import FormServicesFactory from "../services/FormServicesFactory";
import MediaModal from "../components/modal/MediaModal";
import type { CustomerResource, PendingCustomerResource } from "../services/CustomerResourceService";
import CustomerResourceService from "../services/CustomerResourceService";
import type { Issue } from "../state/validation";
import { createDynamicValueOptions } from "../utils/menu/searchableMenuUtil";
import { getDefaultPinOptions, type Pin, type PinIcon } from "../utils/pinUtil";

export type PinPropertiesProps = {
  pin: Pin;
  onChange: (patch: Partial<Pin>) => void;
  issues: Issue[] | undefined;
};

const PinProperties = (props: PinPropertiesProps) => {
  const { t } = useTranslation();
  const { currentScope, variableService } = useFormServices();
  const { open } = useModalManager();
  const owner = getOwner();

  const [oldPin, setOldPin] = createSignal<Pin | null>(null);
  const [progress, setProgress] = createSignal<number>(0);
  const [abortController, setAbortController] = createSignal<AbortController>();

  const onFileSelected = async (file: PendingCustomerResource) =>
    CustomerResourceService.upload(
      file,
      ({ progress }) => setProgress(!isNil(progress) ? progress * 100 : 0),
      setAbortController(new AbortController()).signal,
    );

  const onDelete = async (file: CustomerResource) => CustomerResourceService.delete(file.id);

  const onCancel = () => {
    const previous = oldPin();
    if (!isNil(previous)) {
      props.onChange(previous);
      setOldPin(null);
    }

    // Abort uploads if there was one in progress
    abortController()?.abort();
  };

  const subformOptions = createMemo(() =>
    SubformService.getAllowedSubforms(currentScope).map(
      (subform) =>
        ({
          variant: "icon",
          value: subform.uid,
          label: subform.meta.name,
          icon: { name: "documentDuplicate", type: "solid", class: "icon-primary" },
        }) satisfies SelectOption<SubformId>,
    ),
  );

  const summarizableFields = createMemo(() =>
    isNil(props.pin.target_form_id)
      ? []
      : FormServicesFactory.forSubform(props.pin.target_form_id).fieldService.getFieldsByTypes(SUMMARIZABLE_FIELDS),
  );
  const summarizableVariables = createMemo(() => variableService.getVariablesByTypes(SUMMARIZABLE_VARIABLES));
  const mixedInputOptions = createMemo(() =>
    createDynamicValueOptions(t, {
      fields: summarizableFields().flatMap((f) => toFieldValueOptions(f)),
      subforms: [],
      customVariables: summarizableVariables().map(toVariableValueOption),
      metaVariables: [],
    }),
  );

  const iconOptions = createMemo((): SelectOption<PinIcon>[] => {
    const defaultOptions = getDefaultPinOptions(t);
    if (props.pin.icon?.type !== "RESOURCE") {
      return defaultOptions;
    }

    const customIcon: ImageOption<PinIcon> = {
      variant: "image",
      value: props.pin.icon,
      label: t(($) => $.FIELD_PROPERTY_PIN_CUSTOM_ICON),
      image: {
        resourceId: props.pin.icon.value,
        alt: t(($) => $.FIELD_PROPERTY_PIN_CUSTOM_ICON),
      },
    };

    return [...defaultOptions, customIcon];
  });

  const nameError = createMemo(
    () => props.issues?.find((issue) => (issue.path ?? []).some((p) => p.key === "name"))?.message,
  );
  const summaryError = createMemo(
    () => props.issues?.find((issue) => (issue.path ?? []).some((p) => p.key === "itemMarkup"))?.message,
  );

  return (
    <div class="flex flex-col gap-y-4 pt-4">
      <Input label={t(($) => $.FIELD_PROPERTY_PIN_ICON)}>
        {() => (
          <div class="flex gap-x-1">
            <div class="w-full">
              <SingleSelect
                hasError={false}
                options={iconOptions()}
                onSelect={(icon) => props.onChange({ icon })}
                sameWidth
                ariaLabel={t(($) => $.FIELD_PROPERTY_PIN_ICON)}
                value={props.pin.icon}
              />
            </div>
            <TextAndIconButton
              text={t(($) => $.FIELD_PROPERTY_PIN_CUSTOM_ICON)}
              icon="image"
              variant="secondary"
              onClick={() => {
                setOldPin(props.pin);
                open({
                  content: () => (
                    <MediaModal
                      progress={progress()}
                      setProgress={setProgress}
                      onDelete={onDelete}
                      onCancel={onCancel}
                      fetchPage={(page, query) => CustomerResourceService.fetchPage(page, { query })}
                      mediaUpload={{ onFileSelected }}
                      onSelect={(file) => props.onChange({ icon: { type: "RESOURCE", value: file.id } })}
                      defaultTab="library"
                    />
                  ),
                });
              }}
            />
          </div>
        )}
      </Input>

      <TextInput
        label={t(($) => $.FIELD_PROPERTY_PIN_NAME)}
        value={props.pin.name}
        onBlur={(e) => props.onChange({ name: e.currentTarget.value })}
        errorMessage={nameError()}
        required
      />

      <div class="flex w-full gap-x-2">
        <div class="min-w-0 flex-1">
          <SingleSelectInput
            label={t(($) => $.FIELD_PROPERTY_SUBFORM_TARGET_FORM_ID)}
            placeholder={t(($) => $.FIELD_PROPERTY_SUBFORM_TARGET_FORM_ID_PLACEHOLDER)}
            value={props.pin.target_form_id}
            options={subformOptions()}
            onSelect={(value) => props.onChange({ target_form_id: value })}
            onClear={() => props.onChange({ target_form_id: null })}
            sameWidth
          />
        </div>

        <div class="mt-6 flex shrink-0 items-center gap-x-2">
          <Show
            when={props.pin.target_form_id}
            fallback={
              <>
                <span class="text-header-sm text-secondary uppercase">
                  {t(($) => $.FIELD_PROPERTY_SUBFORM_TARGET_FORM_ID_OR)}
                </span>
                <TextAndIconButton
                  variant="light"
                  icon="plus"
                  text={t(($) => $.FIELD_PROPERTY_SUBFORM_TARGET_FORM_ID_CREATE)}
                  onClick={() => {
                    open({
                      skipClosingAnimation: true,
                      content: () => (
                        <SubformCreateModal
                          onCreate={(subform) => {
                            props.onChange({ target_form_id: subform.uid });
                            new SubformShowEvent(subform.uid).send();
                          }}
                        />
                      ),
                    });
                  }}
                />
              </>
            }
          >
            {(subformId) => (
              <TextAndIconButton
                text={t(($) => $.FIELD_PROPERTY_SUBFORM_TARGET_FORM_ID_EDIT)}
                icon="pencil"
                variant="secondary"
                onClick={() => {
                  runWithOwner(owner, () => new SubformShowEvent(subformId()).send());
                }}
              />
            )}
          </Show>
        </div>
      </div>

      <MixedInput
        label={t(($) => $.FIELD_PROPERTY_PIN_ITEM_MARKUP)}
        value={props.pin.itemMarkup}
        parse={(value) => parseStringTemplate(value, summarizableFields(), summarizableVariables())}
        onChange={(value) => props.onChange({ itemMarkup: value })}
        options={mixedInputOptions()}
        info={{
          title: t(($) => $.FIELD_PROPERTY_PIN_ITEM_MARKUP),
          alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: "props.propertyKeys.itemHtml" }),
          description: t(($) => $.FIELD_PROPERTY_SUBFORM_SUMMARY_IN_APP_DESCRIPTION),
        }}
        errorMessage={summaryError()}
        required
      />
    </div>
  );
};

export default PinProperties;
